import React from "react";

/* Minimal, safe markdown renderer for chat replies.

   The agent writes light markdown — **bold**, `code`, *italic*, "- "/"* " bullet
   lists, "1." numbered lists and blank-line paragraphs. Rendering it as raw text
   leaks the asterisks/backticks; rendering it with dangerouslySetInnerHTML would
   invite XSS. So we parse the small subset we actually need and build real React
   elements — no HTML injection, no extra dependency. */

const INLINE_RE = /(\*\*[^*]+\*\*|__[^_]+__|`[^`]+`|\*[^*\n]+\*)/g;

function renderInline(text, keyPrefix) {
  const out = [];
  let last = 0;
  let i = 0;
  let m;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyPrefix}-i${i++}`;
    if (tok.startsWith("**") || tok.startsWith("__")) {
      out.push(
        <strong key={key} className="font-semibold text-slate-900">
          {tok.slice(2, -2)}
        </strong>
      );
    } else if (tok.startsWith("`")) {
      out.push(
        <code
          key={key}
          className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[0.85em] text-slate-800 ring-1 ring-inset ring-slate-200"
        >
          {tok.slice(1, -1)}
        </code>
      );
    } else {
      out.push(
        <em key={key} className="italic">
          {tok.slice(1, -1)}
        </em>
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function ChatMarkdown({ text = "", className = "" }) {
  const lines = String(text).replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let list = null; // { ordered, items: [] }
  let para = [];

  const flushPara = () => {
    if (para.length) {
      blocks.push({ type: "p", text: para.join(" ") });
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push({ type: "list", ordered: list.ordered, start: list.start, items: list.items });
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const numbered = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
    const heading = line.match(/^\s*(#{1,4})\s+(.*)$/);

    if (!line.trim()) {
      flushPara();
      flushList();
      continue;
    }
    if (heading) {
      flushPara();
      flushList();
      blocks.push({ type: "h", text: heading[2] });
      continue;
    }
    // An indented bullet under a numbered item is that item's sub-point, so
    // the numbering goes on after it (1., its bullets, 2.).
    if (bullet && list && list.ordered && /^\s{2,}/.test(raw)) {
      list.items[list.items.length - 1].sub.push(bullet[1]);
      continue;
    }
    if (bullet) {
      flushPara();
      if (!list || list.ordered) {
        flushList();
        list = { ordered: false, items: [] };
      }
      list.items.push({ text: bullet[1], sub: [] });
      continue;
    }
    if (numbered) {
      flushPara();
      if (!list || !list.ordered) {
        flushList();
        list = { ordered: true, start: Number(numbered[1]) || 1, items: [] };
      }
      list.items.push({ text: numbered[2], sub: [] });
      continue;
    }
    // Continuation of a list item, or a normal paragraph line.
    if (list && /^\s{2,}\S/.test(raw)) {
      const item = list.items[list.items.length - 1];
      if (item.sub.length) item.sub[item.sub.length - 1] += ` ${line.trim()}`;
      else item.text += ` ${line.trim()}`;
      continue;
    }
    flushList();
    para.push(line.trim());
  }
  flushPara();
  flushList();

  return (
    <div className={className}>
      {blocks.map((b, bi) => {
        if (b.type === "h") {
          return (
            <p key={bi} className="mb-1.5 mt-3 font-semibold text-slate-900 first:mt-0">
              {renderInline(b.text, `h${bi}`)}
            </p>
          );
        }
        if (b.type === "list") {
          const Tag = b.ordered ? "ol" : "ul";
          return (
            <Tag
              key={bi}
              start={b.ordered && b.start !== 1 ? b.start : undefined}
              className={[
                "my-2 space-y-1 pl-5 first:mt-0 last:mb-0",
                b.ordered ? "list-decimal" : "list-disc",
              ].join(" ")}
            >
              {b.items.map((it, ii) => (
                <li key={ii} className="pl-0.5 marker:text-slate-400">
                  {renderInline(it.text, `l${bi}-${ii}`)}
                  {it.sub.length > 0 && (
                    <ul className="mt-1 list-disc space-y-1 pl-4">
                      {it.sub.map((x, si) => (
                        <li key={si} className="marker:text-slate-300">
                          {renderInline(x, `l${bi}-${ii}-${si}`)}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </Tag>
          );
        }
        return (
          <p key={bi} className="my-2 first:mt-0 last:mb-0">
            {renderInline(b.text, `p${bi}`)}
          </p>
        );
      })}
    </div>
  );
}
